import { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Box,
  Chip,
  Pagination,
  Stack,
  Divider,
  Avatar,
  Accordion,          
  AccordionSummary,    
  AccordionDetails,
  Container
} from "@mui/material";

// Modern Icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"; 

import { api } from "../api/api";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 10; // ✅ You can change this number to show more/less items per page

  const vendorId = localStorage.getItem("vendor_id");

  // ✅ LOAD ORDERS (Wrapped in useCallback)
  const loadOrders = useCallback(async () => {
    try {
      const res = await api.get(`/vendor/${vendorId}/orders`);
      
      // FILTER TO ONLY SHOW "DELIVERED" ORDERS
      const deliveredOrders = res.data.filter((o) => o.status === "DELIVERED");
      setOrders(deliveredOrders);
    } catch (err) {
      console.log("Error loading orders:", err);
    }
  }, [vendorId]);

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 5000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  const totalPages = Math.ceil(orders.length / ITEMS_PER_PAGE);

  // ✅ BULLETPROOFING: If data updates and the current page no longer exists, safely snap back to the last available page.
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  // ✅ Slicing the data for the current page
  const displayedOrders = orders.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // ✅ UX IMPROVEMENT: Smoothly scroll to top when changing pages
  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8", pt: { xs: 2, sm: 3 }, pb: 10, px: { xs: 2, sm: 3, md: 4 } }}>
      <Container maxWidth="md" disableGutters>
        
        <Box display="flex" alignItems="center" gap={2} mb={4}>
          <Avatar sx={{ bgcolor: "#FFF0EE", color: "#3A2C23", width: 48, height: 48 }}>
            <ReceiptLongIcon />
          </Avatar>
          <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", letterSpacing: "-0.5px" }}>
            Order History
          </Typography>
        </Box>

        {orders.length === 0 && (
          <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#FFFFFF', borderRadius: '24px', border: '1px dashed #ECE5DA' }}>
            <Typography color="#A59D96" fontWeight="700">
              No delivered orders yet.
            </Typography>
          </Box>
        )}

        {displayedOrders.map((o) => {
          return (
            <Accordion 
              key={o.order_id} 
              disableGutters
              elevation={0}
              sx={{ 
                mb: 2.5, 
                borderRadius: "24px !important", 
                bgcolor: "#FFFFFF",
                border: "none", 
                boxShadow: "0 10px 24px rgba(0, 0, 0, 0.08)",
                overflow: "hidden",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 28px rgba(0, 0, 0, 0.1)",
                },
                "&:before": { display: "none" }, 
              }}
            >
              {/* HEADER (Always visible) */}
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#3A2C23" }} />}
                sx={{ 
                  bgcolor: "#FFFFFF", 
                  px: { xs: 2, sm: 3 }, 
                  py: 1, 
                  borderBottom: "1px solid transparent",
                  "&.Mui-expanded": {
                    borderBottom: "1px dashed #ECE5DA",
                  },
                  minHeight: "48px", 
                  "& .MuiAccordionSummary-content": { my: 1.5 } 
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={1}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <CheckCircleIcon sx={{ color: "#577A34", fontSize: 24 }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, display: 'block', lineHeight: 1 }}>
                        ORDER ID
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="900" color="#3A2C23" sx={{ lineHeight: 1.2, mt: 0.2 }}>
                        #{o.order_id}
                      </Typography>
                    </Box>
                  </Stack>
                  
                  <Chip
                    label="DELIVERED"
                    size="small"
                    sx={{ 
                      fontWeight: "800", 
                      bgcolor: "#E1EED3", 
                      color: "#577A34", 
                      borderRadius: '8px',
                      px: 1,
                      letterSpacing: 0.5,
                      fontSize: "0.7rem", 
                      height: "24px"
                    }}
                  />
                </Box>
              </AccordionSummary>

              {/* BODY (Hidden until clicked) */}
              <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#FFFFFF" }}>
                <Stack spacing={1.5} mb={1.5}>
                  {o.items.map((i, idx) => (
                    <Stack 
                      direction="row" 
                      justifyContent="space-between" 
                      alignItems="center" 
                      key={idx} 
                      sx={{ 
                        p: 1.5, 
                        bgcolor: '#FFFDF8', 
                        border: '1px solid #ECE5DA', 
                        borderRadius: '12px' 
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: "#FFF0EE", color: "#E85D3F" }}>
                          <RestaurantIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box>
                          <Typography fontWeight="800" color="#2F2A25" sx={{ lineHeight: 1.2, fontSize: "0.95rem" }}>
                            {i.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6E655E", fontWeight: "700" }}>
                            ₹{i.vendor_price} per item
                          </Typography>
                        </Box>
                      </Stack>

                      <Box textAlign="right">
                        <Typography fontWeight="900" color="#3A2C23" fontSize="0.95rem">
                          ₹{i.vendor_price * i.quantity}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800' }}>
                          Qty: {i.quantity}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
                
                <Divider sx={{ my: 2, borderStyle: "dashed", borderColor: "#ECE5DA" }} />

                {/* HIGHLIGHTED TOTAL BOX */}
                <Box 
                  display="flex" 
                  justifyContent="space-between" 
                  alignItems="center" 
                  sx={{ 
                    bgcolor: "#FFF0EE", 
                    px: 2.5, 
                    py: 1.5, 
                    borderRadius: "14px",
                    border: "1px solid #FFE4E0"
                  }}
                >
                   <Typography variant="caption" fontWeight="800" color="#E85D3F" sx={{ letterSpacing: 0.5 }}> 
                       GRAND TOTAL
                   </Typography>
                   <Typography variant="h6" fontWeight="900" color="#E85D3F"> 
                       ₹{o.total_vendor_price}
                   </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {/* ✅ PAGINATION CONTROLS */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={4} mb={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange} 
              sx={{
                "& .MuiPaginationItem-root": { fontWeight: "800", color: "#6E655E", borderRadius: '8px' },
                "& .Mui-selected": { bgcolor: "#3A2C23 !important", color: "#FFFFFF" }
              }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
}